import React from "react";
import "@/App.css";
import "@/lib/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { CurrencyProvider } from "@/lib/currency";
import { SiteContentProvider } from "@/lib/siteContent";
import HomePage from "@/pages/HomePage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import HotelDetailPage from "@/pages/HotelDetailPage";
import DestinationLandingPage from "@/pages/DestinationLandingPage";
import CheckoutPage from "@/pages/CheckoutPage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import LoginPage from "@/pages/LoginPage";
import StaffLoginPage from "@/pages/StaffLoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MyBookingsPage from "@/pages/MyBookingsPage";
import AdminPage from "@/pages/AdminPage";
import AdminHotelsPage from "@/pages/AdminHotelsPage";
import AdminHotelFormPage from "@/pages/AdminHotelFormPage";
import AdminRatePlansPage from "@/pages/AdminRatePlansPage";
import AdminHotelWidgetPage from "@/pages/AdminHotelWidgetPage";
import AdminCalendarPage from "@/pages/AdminCalendarPage";
import AdminProfilePage from "@/pages/AdminProfilePage";
import AdminReviewsPage from "@/pages/AdminReviewsPage";
import AdminDestinationsPage from "@/pages/AdminDestinationsPage";
import AdminBookingsPage from "@/pages/AdminBookingsPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminSiteContentPage from "@/pages/AdminSiteContentPage";
import AdminCmsPagesPage from "@/pages/AdminCmsPagesPage";
import AdminCmsPageEditPage from "@/pages/AdminCmsPageEditPage";
import AdminAdsPage from "@/pages/AdminAdsPage";
import AdminCouponsPage from "@/pages/AdminCouponsPage";
import AdminRedirectsPage from "@/pages/AdminRedirectsPage";
import AdminMediaLibraryPage from "@/pages/AdminMediaLibraryPage";
import AdminNewsletterPage from "@/pages/AdminNewsletterPage";
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";
import AdminFeatureFlagsPage from "@/pages/AdminFeatureFlagsPage";
import CmsPageView from "@/pages/CmsPageView";
import RedirectGuard from "@/components/RedirectGuard";
import { FeatureFlagsProvider } from "@/lib/featureFlags";
import AdminYieldManagerPage from "@/pages/AdminYieldManagerPage";
import StaticPage from "@/pages/StaticPage";
import AdminFinancePage from "@/pages/AdminFinancePage";
import AdminRefundsPage from "@/pages/AdminRefundsPage";
import FinanceDashboardPage from "@/pages/admin/finance/FinanceDashboardPage";
import LedgerPage from "@/pages/admin/finance/LedgerPage";
import PartiesPage from "@/pages/admin/finance/PartiesPage";
import PartyDetailPage from "@/pages/admin/finance/PartyDetailPage";
import CashAccountsPage from "@/pages/admin/finance/CashAccountsPage";
import RefundsPage from "@/pages/admin/finance/RefundsPage";
import InvoicesPage from "@/pages/admin/finance/InvoicesPage";
import FinanceReportsPage from "@/pages/admin/finance/ReportsPage";
import PeriodClosePage from "@/pages/admin/finance/PeriodClosePage";
import HotelConfirmPage from "@/pages/HotelConfirmPage";
import AdminSupportPage from "@/pages/AdminSupportPage";
import ApplicationTrackPage from "@/pages/ApplicationTrackPage";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import HotelDashboardPage from "@/pages/HotelDashboardPage";
import HotelBookingsPage from "@/pages/HotelBookingsPage";
import HotelReviewsPage from "@/pages/HotelReviewsPage";
import HotelFinancePage from "@/pages/HotelFinancePage";
import HotelStaffPage from "@/pages/HotelStaffPage";
import HotelSettingsPage from "@/pages/HotelSettingsPage";
import HotelCalendarRedirect from "@/pages/HotelCalendarRedirect";
import HotelLoginPage from "@/pages/HotelLoginPage";
import HotelTodayPage from "@/pages/HotelTodayPage";
import HotelPosPage from "@/pages/HotelPosPage";
import HotelStatsPage from "@/pages/HotelStatsPage";
import HotelPromotionsPage from "@/pages/HotelPromotionsPage";
import HotelManualBookingPage from "@/pages/HotelManualBookingPage";
import HotelAmenitiesPage from "@/pages/HotelAmenitiesPage";
import { AdminHotelPosPage, AdminHotelPromotionsPage } from "@/pages/AdminHotelScopedPages";
import RegisterPropertyPage from "@/pages/RegisterPropertyPage";
import AdminHotelApplicationsPage from "@/pages/AdminHotelApplicationsPage";
import { isAdminHost, isPublicHost, HAS_SPLIT_HOSTS } from "@/lib/hostMode";
import { Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import { useTracking } from "@/lib/analytics";

function AnalyticsTracker() {
    useTracking();
    return null;
}

// Guard: on the admin host, public routes redirect to /login.
function PublicOnly({ children }) {
    if (HAS_SPLIT_HOSTS && !isPublicHost()) return <Navigate to="/login" replace />;
    return children;
}

// Guard: on the public host, admin routes redirect to the admin subdomain.
function AdminOnly({ children }) {
    if (HAS_SPLIT_HOSTS && !isAdminHost()) {
        const adminHost = process.env.REACT_APP_ADMIN_HOST;
        if (typeof window !== "undefined" && adminHost) {
            window.location.href = `https://${adminHost}${window.location.pathname}`;
            return null;
        }
    }
    return children;
}

function App() {
    return (
        <div className="App">
            <Toaster position="top-right" richColors closeButton />
            <BrowserRouter>
                <AuthProvider>
                    <ForcePasswordChange />
                    <CurrencyProvider>
                        <SiteContentProvider>
                        <FeatureFlagsProvider>
                        <RedirectGuard />
                        <AnalyticsTracker />
                        <Routes>
                        <Route path="/" element={<PublicOnly><HomePage /></PublicOnly>} />
                        <Route path="/search" element={<PublicOnly><SearchResultsPage /></PublicOnly>} />
                        <Route path="/hotel/:id" element={<PublicOnly><HotelDetailPage /></PublicOnly>} />
                        <Route path="/destinasyon/:slug" element={<PublicOnly><DestinationLandingPage /></PublicOnly>} />
                        <Route path="/destination/:slug" element={<PublicOnly><DestinationLandingPage /></PublicOnly>} />
                        <Route path="/checkout" element={<PublicOnly><CheckoutPage /></PublicOnly>} />
                        <Route path="/booking-success" element={<PublicOnly><BookingSuccessPage /></PublicOnly>} />
                        <Route path="/hotel-confirm/:token" element={<HotelConfirmPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/staff-login" element={<StaffLoginPage />} />
                        <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
                        <Route path="/my-bookings" element={<PublicOnly><MyBookingsPage /></PublicOnly>} />
                        <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
                        <Route path="/admin/hotels" element={<AdminOnly><AdminHotelsPage /></AdminOnly>} />
                        <Route path="/admin/hotels/new" element={<AdminOnly><AdminHotelFormPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/edit" element={<AdminOnly><AdminHotelFormPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/rates" element={<AdminOnly><AdminRatePlansPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/calendar" element={<AdminOnly><AdminCalendarPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/widget" element={<AdminOnly><AdminHotelWidgetPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/pos-config" element={<AdminOnly><AdminHotelPosPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:id/promotions" element={<AdminOnly><AdminHotelPromotionsPage /></AdminOnly>} />
                        <Route path="/admin/destinations" element={<AdminOnly><AdminDestinationsPage /></AdminOnly>} />
                        <Route path="/admin/reviews" element={<AdminOnly><AdminReviewsPage /></AdminOnly>} />
                        <Route path="/admin/bookings" element={<AdminOnly><AdminBookingsPage /></AdminOnly>} />
                        <Route path="/admin/hotels/:hotelId/yield" element={<AdminOnly><AdminYieldManagerPage /></AdminOnly>} />
                        <Route path="/admin/users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
                        <Route path="/basvuru-takip" element={<ApplicationTrackPage />} />
                        <Route path="/admin/support" element={<AdminOnly><AdminSupportPage /></AdminOnly>} />
                        <Route path="/admin/finance" element={<AdminOnly><FinanceDashboardPage /></AdminOnly>} />

                        <Route path="/admin/finance/payments" element={<AdminOnly><AdminFinancePage /></AdminOnly>} />
                        <Route path="/admin/refunds" element={<AdminOnly><AdminRefundsPage /></AdminOnly>} />
                        <Route path="/admin/finance/ledger" element={<AdminOnly><LedgerPage /></AdminOnly>} />
                        <Route path="/admin/finance/parties" element={<AdminOnly><PartiesPage /></AdminOnly>} />
                        <Route path="/admin/finance/parties/:id" element={<AdminOnly><PartyDetailPage /></AdminOnly>} />
                        <Route path="/admin/finance/cash" element={<AdminOnly><CashAccountsPage /></AdminOnly>} />
                        <Route path="/admin/finance/refunds" element={<AdminOnly><RefundsPage /></AdminOnly>} />
                        <Route path="/admin/finance/invoices" element={<AdminOnly><InvoicesPage /></AdminOnly>} />
                        <Route path="/admin/finance/reports" element={<AdminOnly><FinanceReportsPage /></AdminOnly>} />
                        <Route path="/admin/finance/period-close" element={<AdminOnly><PeriodClosePage /></AdminOnly>} />
                        <Route path="/admin/reports" element={<AdminOnly><AdminReportsPage /></AdminOnly>} />
                        <Route path="/admin/analytics" element={<AdminOnly><AdminAnalyticsPage /></AdminOnly>} />
                        <Route path="/admin/settings" element={<AdminOnly><AdminSettingsPage /></AdminOnly>} />
                        <Route path="/admin/profile" element={<AdminOnly><AdminProfilePage /></AdminOnly>} />
                        <Route path="/admin/site-content" element={<AdminOnly><AdminSiteContentPage /></AdminOnly>} />
                        <Route path="/admin/cms-pages" element={<AdminOnly><AdminCmsPagesPage /></AdminOnly>} />
                        <Route path="/admin/cms-pages/:id" element={<AdminOnly><AdminCmsPageEditPage /></AdminOnly>} />
                        <Route path="/admin/ads" element={<AdminOnly><AdminAdsPage /></AdminOnly>} />
                        <Route path="/admin/coupons" element={<AdminOnly><AdminCouponsPage /></AdminOnly>} />
                        <Route path="/admin/redirects" element={<AdminOnly><AdminRedirectsPage /></AdminOnly>} />
                        <Route path="/admin/media" element={<AdminOnly><AdminMediaLibraryPage /></AdminOnly>} />
                        <Route path="/admin/newsletter" element={<AdminOnly><AdminNewsletterPage /></AdminOnly>} />
                        <Route path="/admin/audit-log" element={<AdminOnly><AdminAuditLogPage /></AdminOnly>} />
                        <Route path="/admin/feature-flags" element={<AdminOnly><AdminFeatureFlagsPage /></AdminOnly>} />
                        <Route path="/p/:slug" element={<CmsPageView />} />
                        {/* Hotel Panel (Faz 1) — otel-scoped admin for otel_* roles */}
                        <Route path="/h/login" element={<HotelLoginPage />} />
                        <Route path="/tesisimi-kaydet" element={<RegisterPropertyPage />} />
                        <Route path="/register-property" element={<RegisterPropertyPage />} />
                        <Route path="/admin/hotel-applications" element={<AdminOnly><AdminHotelApplicationsPage /></AdminOnly>} />
                        <Route path="/h/dashboard" element={<AdminOnly><HotelDashboardPage /></AdminOnly>} />
                        <Route path="/h/today" element={<AdminOnly><HotelTodayPage /></AdminOnly>} />
                        <Route path="/h/bookings" element={<AdminOnly><HotelBookingsPage /></AdminOnly>} />
                        <Route path="/h/manual-booking" element={<AdminOnly><HotelManualBookingPage /></AdminOnly>} />
                        <Route path="/h/reviews" element={<AdminOnly><HotelReviewsPage /></AdminOnly>} />
                        <Route path="/h/finance" element={<AdminOnly><HotelFinancePage /></AdminOnly>} />
                        <Route path="/h/staff" element={<AdminOnly><HotelStaffPage /></AdminOnly>} />
                        <Route path="/h/settings" element={<AdminOnly><HotelSettingsPage /></AdminOnly>} />
                        <Route path="/h/calendar" element={<AdminOnly><HotelCalendarRedirect /></AdminOnly>} />
                        <Route path="/h/yield" element={<AdminOnly><HotelCalendarRedirect /></AdminOnly>} />
                        <Route path="/h/pos" element={<AdminOnly><HotelPosPage /></AdminOnly>} />
                        <Route path="/h/stats" element={<AdminOnly><HotelStatsPage /></AdminOnly>} />
                        <Route path="/h/amenities" element={<AdminOnly><HotelAmenitiesPage /></AdminOnly>} />
                        <Route path="/h/promotions" element={<AdminOnly><HotelPromotionsPage /></AdminOnly>} />
                        <Route path="/page/:slug" element={<PublicOnly><StaticPage /></PublicOnly>} />
                        </Routes>
                        </FeatureFlagsProvider>
                        </SiteContentProvider>
                    </CurrencyProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
